import { lazy, type ComponentType } from 'react';

/**
 * Wraps React.lazy with a single-retry mechanism for chunk load failures.
 * When a new deployment invalidates old chunk hashes, the stale SW cache
 * serves 404s. This helper catches that error and reloads the page once
 * to pick up the new manifest. A sessionStorage flag prevents infinite loops.
 */
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const key = 'aphenis_chunk_retry';
      const already = sessionStorage.getItem(key);
      if (!already) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        // Return a never-resolving promise so React doesn't render the error
        return new Promise<{ default: T }>(() => {});
      }
      // Already retried once this session — clear flag and propagate
      sessionStorage.removeItem(key);
      throw err;
    })
  );
}
